import React, { useState, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  InfoTooltipButton,
  Checkbox as DlsCheckBox,
  Label,
  ComponentLevelNotification,
} from '@americanexpress/dls-react';
import {
  parseHtml,
  checkIfValueExist,
  checkIfMandatoryValidationExists,
} from '../../utils/util';
import { validateCheckbox } from '../../Validations/Validations';
import { performOperations } from '../../Operations/Operations';

const getVal = require('lodash/get');

/**
 * returns DLS checkbox component
 * @param {*} props
 */
/* eslint complexity: ["error", 24] -- need to increase complexity */
const Checkbox = (props) => {
  const {
    fieldId,
    fieldLabel,
    cssProperties,
    fieldValidation,
    checkConsent = {},
    setSelectedOptions,
    selectedOptions = {},
    isHiddenRequired = true,
    isMultipleTabsData = false,
    formSubmissionPayload,
    setFormSubmissionPayload,
    selectedOptionLabels,
    setSelectedOptionLabels,
    filledFormData,
    buttonsToDisable,
    disabledButtons,
    setDisabledButtons,
    uncheckedValue = 'No',
    comments,
    setCurPageValues,
    curPageValues,
    validations = [],
    groupDynamicRequired,
    isFilledValueDisabled,
    defaultValue = 'No',
    dispatchIdsState,
    idsToClear,
    fieldDynamicRequired = null,
    setShowPopUp,
    getReviewDisabled,
    operationsToPerform,
    changePageValues,
    setChangePageValues,
    entityToEdit,
    dataObject,
  } = props;

  const [optionCheck, setOptionCheck] = useState(false);
  const [value, setValue] = useState(uncheckedValue);
  const [fieldIdValue, setFieldIdValue] = useState('');
  const [forceRequired, setForceRequired] = useState(false);
  const customInvalidMsg = fieldValidation && fieldValidation.customInvalidMsg
    ? fieldValidation.customInvalidMsg
    : 'Please tick this box if you want to proceed';
  const [showCustomInvalidMsg, setShowCustomInvalidMsg] = useState(false);
  const [customValidations, setCustomValidations] = useState([...validations]);
  const [validationErrState, dispatchErrState] = useReducer(
    (prevState, payload) => ({ ...prevState, ...payload }),
    { validationErr: '', isValid: true }
  );
  const [valueChanged, setValueChanged] = useState(false);
  const payloadFieldValue = filledFormData ? filledFormData[fieldId] : undefined;

  const performCustomValidations = (params) => {
    const { value: checkBoxValue, validationsToPerform } = params;
    const invalidMsg = validateCheckbox(checkBoxValue, validationsToPerform, {
      isFieldRequired: fieldValidation.required,
    });
    if (invalidMsg) {
      dispatchErrState({ validationErr: `${invalidMsg}`, isValid: false });
    } else {
      dispatchErrState({ validationErr: '', isValid: true });
    }
  };

  useEffect(() => {
    if (fieldDynamicRequired && fieldValidation && !fieldValidation.required) {
      const mandatoryValidation = validations.find(
        (val) => val.type === 'mandatoryField'
      );
      if (mandatoryValidation) {
        performCustomValidations({
          inputVal: optionCheck,
          validationsToPerform: validations,
        });
      }
    }
  }, [fieldValidation.required]);

  const groupDynamicValidations = (custValidations, group) => {
    const {
      ids: groupIds,
      operation,
      errorMsg,
      uncheckedValue: uncheckedValueGroup,
    } = group;
    const valueExists = checkIfValueExist(
      groupIds,
      formSubmissionPayload,
      uncheckedValueGroup
    );
    const shouldMakeitRequired = operation === 'isEmpty' ? !valueExists : valueExists;
    setForceRequired(shouldMakeitRequired);

    if (shouldMakeitRequired) {
      if (!checkIfMandatoryValidationExists(custValidations)) {
        setCustomValidations([
          {
            type: 'mandatoryField',
            onInvalidMsg: errorMsg,
          },
          ...customValidations,
        ]);
      }
    } else {
      const removedMandatoryValidation = custValidations.filter(
        (customValidation) => customValidation.type !== 'mandatoryField'
      );
      setCustomValidations([...removedMandatoryValidation]);
      dispatchErrState({ validationErr: '', isValid: true });
    }
  };

  useEffect(() => {
    setFieldIdValue(fieldId);
  }, [fieldId]);

  useEffect(() => {
    if (fieldId !== fieldIdValue) {
      setValue('No');
      setOptionCheck(false);
    }

    if (
      filledFormData
      && filledFormData[fieldId]
      && (typeof isFilledValueDisabled === 'undefined' || !isFilledValueDisabled)
    ) {
      setValue(filledFormData[fieldId]);
      if (filledFormData[fieldId] === 'Yes') {
        setOptionCheck(true);
      } else {
        setOptionCheck(false);
      }
    }

    if (defaultValue === 'Yes') {
      setValue('Yes');
      setOptionCheck(true);
    }

    if (isMultipleTabsData) {
      if (selectedOptions && selectedOptions[fieldId]) {
        setValue(selectedOptions[fieldId]);
        setOptionCheck(true);
      }
    }
  }, [fieldId]);

  useEffect(() => {
    const hasEntityEditValue = entityToEdit && typeof entityToEdit[fieldId] !== 'undefined';
    if (hasEntityEditValue || (typeof isFilledValueDisabled !== 'undefined' && isFilledValueDisabled)) {
      return;
    }

    const normalizedPayloadValue = typeof payloadFieldValue === 'string'
      ? payloadFieldValue.trim().toLowerCase()
      : payloadFieldValue;

    if (normalizedPayloadValue === 'yes' || normalizedPayloadValue === true) {
      setValue('Yes');
      setOptionCheck(true);
      return;
    }

    if (
      normalizedPayloadValue === 'off'
      || normalizedPayloadValue === 'no'
      || normalizedPayloadValue === ''
      || normalizedPayloadValue === false
      || normalizedPayloadValue === undefined
      || normalizedPayloadValue === null
    ) {
      setValue(uncheckedValue);
      setOptionCheck(false);
    }
  }, [payloadFieldValue, isFilledValueDisabled, uncheckedValue, entityToEdit, fieldId]);

  useEffect(() => {
    if (entityToEdit && typeof entityToEdit[fieldId] !== 'undefined') {
      const editedValue = entityToEdit[fieldId];
      setValue(editedValue);
      const normalizedEditedValue = typeof editedValue === 'string' ? editedValue.trim().toLowerCase() : editedValue;
      setOptionCheck(normalizedEditedValue === 'yes' || normalizedEditedValue === true);
    }
  }, [entityToEdit, fieldId]);

  useEffect(() => {
    let val = optionCheck;

    // During prefill hydration, payload can already be Yes while local optionCheck
    // is still false for one render cycle. Align local state first to avoid writing Off.
    const hasEntityEditValue = entityToEdit && typeof entityToEdit[fieldId] !== 'undefined';
    const normalizedPayloadValue = typeof payloadFieldValue === 'string'
      ? payloadFieldValue.trim().toLowerCase()
      : payloadFieldValue;
    const payloadSaysChecked = normalizedPayloadValue === 'yes' || normalizedPayloadValue === true;
    if (!valueChanged && !hasEntityEditValue && payloadSaysChecked && !optionCheck) {
      setValue('Yes');
      setOptionCheck(true);
      return;
    }

    if (fieldValidation && fieldValidation.updatePayloadOnChange) {
      val = uncheckedValue;
      if (optionCheck) {
        val = 'Yes';
      }
      formSubmissionPayload[fieldId] = val;
      setFormSubmissionPayload({
        ...formSubmissionPayload,
        elemId: null,
      });
    }
    if (buttonsToDisable && buttonsToDisable.length > 0) {
      buttonsToDisable.forEach((btnId) => {
        if (optionCheck) {
          if (disabledButtons[btnId] && disabledButtons[btnId][fieldId]) {
            delete disabledButtons[btnId][fieldId];
          }
        } else if (disabledButtons[btnId]) {
          disabledButtons[btnId] = { ...disabledButtons[btnId], [fieldId]: 1 };
        } else {
          disabledButtons[btnId] = { [fieldId]: 1 };
        }
      });
      setDisabledButtons({ ...disabledButtons });
    }
    setCurPageValues({ ...curPageValues, [fieldId]: val });
  }, [optionCheck]);

  useEffect(() => {
    if (getVal(idsToClear.ids, fieldId)) {
      setValue('');
      setOptionCheck(false);
      delete idsToClear.ids[fieldId];
      dispatchIdsState({
        toggleState: idsToClear.toggleState,
        ids: idsToClear,
      });
      delete filledFormData[fieldId];
      setFormSubmissionPayload({ ...filledFormData, elemId: null });
    }
  }, [idsToClear.toggleState]);

  useEffect(() => {
    if (groupDynamicRequired) {
      const { group } = groupDynamicRequired;
      if (group) {
        const { ids } = group;
        if (ids.length > 0) {
          groupDynamicValidations(customValidations, group);
        }
      }
    }
  }, [curPageValues]);

  /**
   * handle checkbox click
   */
  const handleCheck = (e) => {
    const optionId = e.currentTarget.id;
    const removedOption = { ...selectedOptions };
    const optionLabels = { ...selectedOptionLabels };
    if (isMultipleTabsData) {
      if (Object.keys(selectedOptions).length === 0) {
        setSelectedOptions({ [optionId]: `${fieldLabel}` });
        setSelectedOptionLabels({ [optionId]: fieldLabel });
        e.target.value = 'Yes';
      } else if (selectedOptions[optionId]) {
        delete removedOption[optionId];
        delete optionLabels[optionId];
        setSelectedOptions(removedOption);
        setFormSubmissionPayload({
          ...{
            elemId: null,
            selectedOptionsLength: Object.keys(removedOption).length,
          },
        });
      } else {
        setSelectedOptions({ ...selectedOptions, ...{ [optionId]: `${fieldLabel}` } });
        e.target.value = 'Yes';
        let optionsPayload = {};
        if (Object.keys(selectedOptions).length === 4) {
          let indexCounter = 0;
          /* eslint-disable no-restricted-syntax -- need */
          for (const key in selectedOptions) {
            if (key) {
              indexCounter += 1;
              const optionsPayloadObject = {
                [`merchant${indexCounter}Name`]: selectedOptions[key],
                [`merchant${indexCounter}Id`]: key,
              };
              optionsPayload = { ...optionsPayload, ...optionsPayloadObject };
            }
          }
          /* eslint-enable no-restricted-syntax -- need */
          setFormSubmissionPayload({
            ...formSubmissionPayload,
            ...{
              elemId: null,
              selectedOptionsLength: Object.keys(selectedOptions).length + 1,
              ...optionsPayload,
              ...{
                [`merchant${indexCounter + 1}Name`]: fieldLabel,
                [`merchant${indexCounter + 1}Id`]: optionId,
              },
            },
          });
        } else {
          setFormSubmissionPayload({
            ...formSubmissionPayload,
            ...{
              elemId: null,
              selectedOptionsLength: Object.keys(selectedOptions).length + 1,
            },
          });
        }
      }
    }

    if (!optionCheck) {
      if (fieldId === 'userConsent') {
        checkConsent(true);
      }
      setValue('Yes');
      setOptionCheck(true);
      if (fieldValidation?.triggerPopUp) {
        setShowPopUp(fieldValidation.triggerPopUp);
      }
      if (customValidations.length > 0) {
        performCustomValidations({
          value: true,
          validationsToPerform: customValidations,
        });
      }
    } else {
      if (fieldId === 'userConsent') {
        checkConsent(false);
      }
      setOptionCheck(false);
      setValue('No');
    }

    const newCheckedState = !optionCheck;
    const newVal = newCheckedState ? 'Yes' : uncheckedValue;
    if (operationsToPerform?.onChange?.length > 0) {
      performOperations({
        value: newVal,
        operations: operationsToPerform.onChange,
        dataObject: {
          ...dataObject,
          curPageValues,
          setCurPageValues,
          changePageValues,
          setChangePageValues,
          selectedVal: newVal,
        },
        functionObject: { setFormSubmissionPayload },
        setFormSubmissionPayload,
        fieldId,
      });
    }

    // To hide review tool comments
    setValueChanged(true);
  };

  const invalidHandler = (e) => {
    if (customValidations.length > 0) {
      e.preventDefault();
      performCustomValidations({
        value: false,
        validationsToPerform: customValidations,
      });
    } else if (fieldValidation && fieldValidation.hideDefaultValidationMsg) {
      e.preventDefault();
      document.querySelector(`#${fieldId}`).focus();
      setShowCustomInvalidMsg(true);
    } else {
      e.target.setCustomValidity(`${customInvalidMsg}`);
    }
  };

  const onInputHandler = (e) => {
    e.target.setCustomValidity('');
    setShowCustomInvalidMsg(false);
  };

  const showErrorMessage = (errorMessage) => (
    <ComponentLevelNotification id={`${fieldId}-errorMessage`} className="pad-1-t">
      {parseHtml(errorMessage)}
    </ComponentLevelNotification>
  );

  const checkBoxLabel = (
    <Label
      htmlFor={fieldId}
      className={cssProperties.labelClass ? cssProperties.labelClass : 'label-3'}
      id={`${fieldId}-label`}
    >
      {parseHtml(fieldLabel)}
      {fieldValidation && fieldValidation.isToolTip && (
        <>
          <span> </span>
          <Tooltip id="ToolTip">
            <TooltipTrigger>
              <InfoTooltipButton screenReaderLabel="info" />
            </TooltipTrigger>
            <TooltipContent>{fieldValidation.toolTipInfo}</TooltipContent>
          </Tooltip>
        </>
      )}
    </Label>
  );

  return (
    <>
      <div
        className={
          cssProperties.customStyle
            ? `${cssProperties.customStyle} checkbox`
            : 'checkbox'
        }
      >
        <DlsCheckBox
          data-testid={fieldId}
          id={fieldId}
          name={fieldId}
          label={checkBoxLabel}
          onChange={handleCheck}
          checked={optionCheck}
          value={value}
          required={forceRequired ? true : fieldValidation.required}
          onInvalid={invalidHandler}
          onInput={onInputHandler}
          aria-disabled={fieldValidation.disabled || getReviewDisabled(fieldId)
            || !!(
              isMultipleTabsData
              && Object.keys(selectedOptions).length === 5
              && !selectedOptions[fieldId]
            )}
        />
        {!optionCheck && isHiddenRequired && (
          <input type="hidden" id={`${fieldId}-hidden`} name={fieldId} value={value} />
        )}
      </div>
      {!optionCheck
        && fieldValidation
        && fieldValidation.optionNotCheckMsg
        && showErrorMessage(fieldValidation.optionNotCheckMsg)}
      {showCustomInvalidMsg && showErrorMessage(customInvalidMsg)}
      {comments && comments[fieldId] && !valueChanged
        && showErrorMessage(comments[fieldId].comment)}
      {validationErrState.validationErr
        && showErrorMessage(validationErrState.validationErr)}
    </>
  );
};

Checkbox.propTypes = {
  cssProperties: PropTypes.objectOf(PropTypes.shape),
  fieldLabel: PropTypes.string,
  fieldId: PropTypes.string,
  fieldValidation: PropTypes.objectOf(PropTypes.shape),
  formSubmissionPayload: PropTypes.objectOf(PropTypes.shape),
  setFormSubmissionPayload: PropTypes.func,
  selectedOptionLabels: PropTypes.objectOf(PropTypes.shape),
  setSelectedOptionLabels: PropTypes.func,
  setSelectedOptions: PropTypes.func,
  selectedOptions: PropTypes.objectOf(PropTypes.shape),
  isHiddenRequired: PropTypes.bool,
  isMultipleTabsData: PropTypes.bool,
  checkConsent: PropTypes.func,
  filledFormData: PropTypes.objectOf(PropTypes.shape),
  disabledButtons: PropTypes.objectOf(PropTypes.shape),
  setDisabledButtons: PropTypes.func,
  buttonsToDisable: PropTypes.arrayOf(PropTypes.shape),
  uncheckedValue: PropTypes.string,
  comments: PropTypes.objectOf(PropTypes.shape),
  curPageValues: PropTypes.objectOf(PropTypes.shape),
  setCurPageValues: PropTypes.func,
  validations: PropTypes.arrayOf(PropTypes.shape),
  groupDynamicRequired: PropTypes.objectOf(PropTypes.shape),
  isFilledValueDisabled: PropTypes.bool,
  defaultValue: PropTypes.string,
  dispatchIdsState: PropTypes.func,
  idsToClear: PropTypes.objectOf(PropTypes.shape),
  fieldDynamicRequired: PropTypes.objectOf(PropTypes.shape),
  setShowPopUp: PropTypes.func,
  getReviewDisabled: PropTypes.func,
  operationsToPerform: PropTypes.objectOf(PropTypes.shape),
  changePageValues: PropTypes.objectOf(PropTypes.shape),
  setChangePageValues: PropTypes.func,
  entityToEdit: PropTypes.objectOf(PropTypes.shape),
  dataObject: PropTypes.objectOf(PropTypes.shape),
};

export default Checkbox;
